import { useCallback, useEffect, useRef, useState } from "react";
import { merchantInvoiceDashboardSummary } from "../../services/api/merchantInvoiceDashboard.service";
import { merchantInvoiceDashboardRepository } from "../repositories/merchantInvoiceDashboard.repository";

const EMPTY_DASHBOARD_SUMMARY = {
  organization: "",
  org_id: "",
  invoice_summary: {
    daily: [],
    weekly: [],
    monthly: [],
    yearly: [],
  },
  invoice_status_summary: {
    signed: 0,
    unsigned: 0,
    approved: 0,
    rejected: 0,
  },
  invoice_card_count: {
    total: 0,
    signed: 0,
    unsigned: 0,
    approved: 0,
  },
  vendor_onboard_trend: {
    initiated: [],
    onboarded: [],
  },
  vendor_onboard_summary: {
    weekly: [],
    monthly: [],
    yearly: [],
  },
  monthly_invoice_summary: {
    year: new Date().getFullYear(),
    data: [],
  },
};

const inflightSummaryRequests = new Map();

export const useMerchantInvoiceDashboardOffline = (
  authToken,
  orgToken,
  orgId
) => {
  const [data, setData] = useState(EMPTY_DASHBOARD_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastRequestKeyRef = useRef("");

  const loadDashboardSummary = useCallback(
    async (force = false) => {
      if (!orgToken && !orgId) {
        setLoading(false);
        return;
      }

      const requestKey = JSON.stringify({
        orgToken: String(orgToken || ""),
        orgId: String(orgId || ""),
        online: typeof navigator !== "undefined" ? navigator.onLine : false,
      });

      if (!force && lastRequestKeyRef.current === requestKey) return;
      lastRequestKeyRef.current = requestKey;

      setLoading(true);
      setError(null);

      const cacheFallback = async () => {
        const cached = await merchantInvoiceDashboardRepository.getDashboardSummary(
          orgId,
          orgToken
        );
        if (cached?.data) {
          setData({ ...EMPTY_DASHBOARD_SUMMARY, ...cached.data });
          return true;
        }
        setData(EMPTY_DASHBOARD_SUMMARY);
        return false;
      };

      try {
        const isOnline =
          typeof navigator !== "undefined" ? navigator.onLine : false;
        const canCallAPI = isOnline && !!orgToken;

        if (canCallAPI) {
          const inflightKey = `${String(orgToken || "")}|${String(
            authToken || ""
          )}`;
          let requestPromise = inflightSummaryRequests.get(inflightKey);

          if (!requestPromise) {
            requestPromise = merchantInvoiceDashboardSummary(orgToken, authToken);
            inflightSummaryRequests.set(inflightKey, requestPromise);
          }

          let response = {};
          try {
            response = await requestPromise;
          } finally {
            inflightSummaryRequests.delete(inflightKey);
          }

          const normalized = { ...EMPTY_DASHBOARD_SUMMARY, ...response };
          setData(normalized);
          await merchantInvoiceDashboardRepository.saveDashboardSummary(
            normalized,
            orgToken
          );
        } else {
          await cacheFallback();
        }
      } catch (err) {
        setError(err);
        await cacheFallback();
      } finally {
        setLoading(false);
      }
    },
    [authToken, orgToken, orgId]
  );

  useEffect(() => {
    loadDashboardSummary();
  }, [loadDashboardSummary]);

  return {
    data,
    loading,
    error,
    refetch: () => loadDashboardSummary(true),
  };
};
